import { useTranslation } from "react-i18next"

import type { BrandingAssetRef, VoiceStageInk, VoiceStageSetting } from "@/api/admin/branding"
import type { VoiceStageBackground } from "@/features/chat/components/voice-stage/voice-stage-utils"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { VoiceGround } from "@/features/chat/components/voice-stage/voice-ground"
import { resolveStageInk } from "@/features/chat/components/voice-stage/voice-stage-utils"

import { isValidStageColor } from "./voice-stage-background-utils"

// The voice window's own ink and paper (voice-stage.module.css).
const INK_COLOR = "hsl(197 6% 22%)"
const PAPER_COLOR = "hsl(30 50% 98%)"

/** What the preview (and the window) would paint for this value. */
function previewBackground(
  value: VoiceStageSetting,
  image: BrandingAssetRef | null
): VoiceStageBackground {
  if (value.mode === "color" && isValidStageColor(value.color)) {
    return { mode: "color", color: value.color.trim() }
  }
  if (value.mode === "image" && image) {
    return { mode: "image", url: image.versionedUrl, ink: value.ink }
  }
  return { mode: "sky" }
}

type VoiceStageBackgroundSectionProps = {
  value: VoiceStageSetting
  onChange: (next: VoiceStageSetting) => void
  /** The uploaded `voice_stage_background`, if any. */
  image: BrandingAssetRef | null
  /** Where "Colour" starts: the tenant's primary, so the first pick is on brand. */
  suggestedColor: string
  busy: boolean
  onUploadImage: (file: File | undefined) => void
  onRemoveImage: () => void
}

/**
 * Branding for the ground behind the voice window: the brand's sky (default),
 * a flat colour, or an uploaded image. A live preview uses the window's own
 * ground layer, so what the admin sees is what callers get.
 */
export function VoiceStageBackgroundSection({
  value,
  onChange,
  image,
  suggestedColor,
  busy,
  onUploadImage,
  onRemoveImage,
}: VoiceStageBackgroundSectionProps) {
  const { t } = useTranslation()
  const preview = previewBackground(value, image)
  const line = resolveStageInk(preview) === "light" ? PAPER_COLOR : INK_COLOR
  const colorInvalid = value.mode === "color" && !isValidStageColor(value.color)

  const selectMode = (mode: string) => {
    if (mode === "color") onChange({ mode: "color", color: suggestedColor })
    else if (mode === "image") onChange({ mode: "image", ink: "light" })
    else onChange({ mode: "sky" })
  }

  return (
    <section aria-labelledby="branding-voice-stage" className="grid gap-3">
      <h3 className="text-sm font-semibold" id="branding-voice-stage">
        {t("adminDashboard.branding.voiceStage.title", { defaultValue: "Voice window" })}
      </h3>
      <p className="text-muted-foreground text-xs">
        {t("adminDashboard.branding.voiceStage.help", {
          defaultValue:
            "The background behind the voice window. The sky is the default. A colour is shown flat; an image is blurred and comes into focus as the conversation goes, like the sky.",
        })}
      </p>

      <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="grid content-start gap-3">
          <RadioGroup
            value={value.mode}
            aria-label={t("adminDashboard.branding.voiceStage.modeLabel", {
              defaultValue: "Background",
            })}
            onValueChange={selectMode}
          >
            {(
              [
                ["sky", "Sky (default)"],
                ["color", "Colour"],
                ["image", "Image"],
              ] as const
            ).map(([mode, fallback]) => (
              <div key={mode} className="flex items-center gap-2">
                <RadioGroupItem id={`voice-stage-mode-${mode}`} value={mode} />
                <Label className="cursor-pointer" htmlFor={`voice-stage-mode-${mode}`}>
                  {t(`adminDashboard.branding.voiceStage.${mode}`, { defaultValue: fallback })}
                </Label>
              </div>
            ))}
          </RadioGroup>

          {value.mode === "color" ? (
            <div className="grid gap-1.5">
              <Label htmlFor="voice-stage-color-hex">
                {t("adminDashboard.branding.voiceStage.colorLabel", {
                  defaultValue: "Colour (hex)",
                })}
              </Label>
              <div className="flex items-center gap-2">
                <input
                  className="border-input size-9 shrink-0 cursor-pointer rounded border"
                  type="color"
                  value={isValidStageColor(value.color) ? value.color.trim() : "#000000"}
                  aria-label={t("adminDashboard.branding.voiceStage.colorLabel", {
                    defaultValue: "Colour (hex)",
                  })}
                  onChange={(event) => onChange({ mode: "color", color: event.target.value })}
                />
                <Input
                  aria-invalid={colorInvalid}
                  className="font-mono text-xs"
                  id="voice-stage-color-hex"
                  placeholder="#1f6feb"
                  value={value.color}
                  onChange={(event) => onChange({ mode: "color", color: event.target.value })}
                />
              </div>
              {colorInvalid ? (
                <p className="text-destructive text-xs">
                  {t("adminDashboard.branding.voiceStage.colorInvalid", {
                    defaultValue: "Use a hex colour like #1f6feb.",
                  })}
                </p>
              ) : null}
            </div>
          ) : null}

          {value.mode === "image" ? (
            <div className="grid gap-2">
              <Label htmlFor="voice-stage-image">
                {t("adminDashboard.branding.voiceStage.imageLabel", { defaultValue: "Image" })}
              </Label>
              <p className="text-muted-foreground text-xs">
                {t("adminDashboard.branding.voiceStage.imageHelp", {
                  defaultValue:
                    "PNG, JPEG or WebP, landscape, at least 1600 px wide. It is shown blurred until someone speaks.",
                })}
              </p>
              <Input
                accept="image/png,image/jpeg,image/webp"
                disabled={busy}
                id="voice-stage-image"
                type="file"
                onChange={(event) => {
                  onUploadImage(event.target.files?.[0])
                  event.target.value = ""
                }}
              />
              {image ? (
                <Button
                  className="justify-self-start"
                  disabled={busy}
                  size="sm"
                  type="button"
                  variant="ghost"
                  onClick={onRemoveImage}
                >
                  {t("adminDashboard.branding.removeAsset", { defaultValue: "Remove" })}
                </Button>
              ) : (
                <p className="text-destructive text-xs" role="status">
                  {t("adminDashboard.branding.voiceStage.imageMissing", {
                    defaultValue: "Upload an image to use this option.",
                  })}
                </p>
              )}
              <div className="grid gap-1.5">
                <Label id="voice-stage-ink-label">
                  {t("adminDashboard.branding.voiceStage.inkLabel", {
                    defaultValue: "Lines and rings over the image",
                  })}
                </Label>
                <RadioGroup
                  aria-labelledby="voice-stage-ink-label"
                  className="flex gap-4"
                  value={value.ink}
                  onValueChange={(ink) => onChange({ mode: "image", ink: ink as VoiceStageInk })}
                >
                  {(
                    [
                      ["light", "Light"],
                      ["dark", "Dark"],
                    ] as const
                  ).map(([ink, fallback]) => (
                    <div key={ink} className="flex items-center gap-2">
                      <RadioGroupItem id={`voice-stage-ink-${ink}`} value={ink} />
                      <Label className="cursor-pointer" htmlFor={`voice-stage-ink-${ink}`}>
                        {t(
                          `adminDashboard.branding.voiceStage.ink${ink === "light" ? "Light" : "Dark"}`,
                          { defaultValue: fallback }
                        )}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </div>
          ) : null}
        </div>

        <figure className="grid gap-1.5">
          <div
            className="relative h-36 overflow-hidden rounded-lg border"
            data-bg={preview.mode}
            data-testid="voice-stage-preview"
          >
            <VoiceGround background={preview} />
            <div className="relative z-[1] flex h-full flex-col items-center justify-center">
              <span
                className="rounded-full border px-4 py-1 text-sm font-bold"
                style={{ background: PAPER_COLOR, borderColor: INK_COLOR, color: INK_COLOR }}
              >
                {t("adminDashboard.branding.voiceStage.previewWord", { defaultValue: "Listening" })}
              </span>
              <span aria-hidden="true" className="h-5 w-px" style={{ background: line }} />
              <span
                aria-hidden="true"
                className="size-2 rounded-full border"
                style={{ borderColor: line }}
              />
            </div>
          </div>
          <figcaption className="text-muted-foreground text-xs">
            {t("adminDashboard.branding.voiceStage.preview", { defaultValue: "Preview" })}
          </figcaption>
        </figure>
      </div>
    </section>
  )
}
